import React, { useState, useEffect } from 'react'
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Collapse,
  Box,
  Chip,
} from '@material-ui/core'
import { Edit, Delete, Add, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQueryClient } from 'react-query'
import groupsTabStyles from './groupsTab.styles'
import deleteGroup from '../../../Apis/groups/deleteGroup'
import QueryKeys from '../../../utils/constants/queryKeys'
import EditGroupModal from './EditGroupModal/EditGroupModal'
import { parseDate } from '../../../utils/helpers/dateHelpers/parseDate'

const useStyles = makeStyles(groupsTabStyles)

// Format group type for display
const getGroupTypeName = (type) => {
  // Add space before capital letters for display
  return type?.replace(/([A-Z])/g, ' $1').trim() || 'Unknown'
}

const GroupsTab = ({ orgId, seasonId, selectedStage, isLoading, onGroupClick, onGamedayClick }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingGroup, setEditingGroup] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState(null)
  const [expandedGroups, setExpandedGroups] = useState({})

  const stageId = selectedStage?.id
  const groups = selectedStage?.groups || []

  const deleteGroupMutation = useMutation(
    (groupId) => deleteGroup({ orgId, seasonId, groupId }),
    {
      onSuccess: () => {
        // Invalidate stages query to refresh groups
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        setDeleteConfirmOpen(false)
        setGroupToDelete(null)
      },
    }
  )

  const handleAddGroup = () => {
    setEditingGroup(null)
    setOpenDialog(true)
  }

  const handleEditGroup = (group) => {
    setEditingGroup(group)
    setOpenDialog(true)
  }

  const handleDeleteGroup = (group) => {
    setGroupToDelete(group)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (groupToDelete) {
      deleteGroupMutation.mutate(groupToDelete.id)
    }
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingGroup(null)
  }

  const toggleGroupExpansion = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }))
  }

  // Initialize all groups as expanded when groups data is loaded
  useEffect(() => {
    if (groups && groups.length > 0) {
      const initialExpandedState = {}
      groups.forEach((group) => {
        // Only expand if the group has gamedays
        if (group.gamedays && group.gamedays.length > 0) {
          initialExpandedState[group.id] = true
        }
      })
      setExpandedGroups(initialExpandedState)
    }
  }, [groups])

  if (!selectedStage) {
    return (
      <div className={classes.container}>
        <Typography>Please select a stage to view groups</Typography>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Button
          variant='contained'
          color='primary'
          startIcon={<Add />}
          onClick={handleAddGroup}
        >
          Add Group
        </Button>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{paddingTop: 10, paddingBottom: 10, width: 50}} />
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Group Name</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Type</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} align='center' style={{ padding: 32 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : groups && groups.length > 0 ? (
              groups.map((group) => {
                const isExpanded = expandedGroups[group.id]
                const gamedays = group.gamedays || []
                const hasGamedays = gamedays.length > 0

                return (
                  <React.Fragment key={group.id}>
                    {/* Group Row */}
                    <TableRow hover className={classes.groupRow}>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleGroupExpansion(group.id)
                          }}
                          disabled={!hasGamedays}
                          className={classes.expandButton}
                        >
                          {hasGamedays && (isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />)}
                        </IconButton>
                      </TableCell>
                      <TableCell onClick={() => onGroupClick(group)}>
                        {group.name}
                        {hasGamedays && (
                          <Chip
                            label={`${gamedays.length} ${gamedays.length === 1 ? 'gameday' : 'gamedays'}`}
                            size='small'
                            className={classes.gamedayChip}
                            color='primary'
                            variant='outlined'
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={() => onGroupClick(group)}>
                        {getGroupTypeName(group.type)}
                      </TableCell>
                      <TableCell align='right'>
                        <div className={classes.actionButtons}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditGroup(group)
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='secondary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteGroup(group)
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Gamedays Row (Collapsible) */}
                    {hasGamedays && (
                      <TableRow>
                        <TableCell className={classes.gamedaysCell} colSpan={4}>
                          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <Box className={classes.gamedaysContainer}>
                              <Typography variant='subtitle2' gutterBottom>
                                Gamedays in this group:
                              </Typography>
                              {gamedays.map((gameday) => {
                                const start = parseDate(gameday.startDate)
                                const end = parseDate(gameday.endDate)
                                let dateLabel = null
                                if (start && end) dateLabel = `${start} - ${end}`
                                else if (start) dateLabel = `Starts ${start}`
                                else if (end) dateLabel = `Ends ${end}`
                                return (
                                  <div
                                    key={gameday.id}
                                    className={classes.gamedayItem}
                                    onClick={() => {
                                      onGroupClick(group)
                                      if (onGamedayClick) onGamedayClick(gameday)
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <Typography variant='body2'>
                                      • {gameday.name}
                                    </Typography>
                                    {dateLabel && (
                                      <Chip
                                        label={dateLabel}
                                        size='small'
                                        className={classes.gamedayChip}
                                      />
                                    )}
                                  </div>
                                )
                              })}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align='center'>
                  No groups found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Group Modal */}
      <EditGroupModal
        isOpen={openDialog}
        onClose={handleCloseDialog}
        groupData={editingGroup}
        orgId={orgId}
        seasonId={seasonId}
        stageId={stageId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete group &quot;{groupToDelete?.name}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='secondary'
            variant='contained'
            disabled={deleteGroupMutation.isLoading}
          >
            {deleteGroupMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default GroupsTab
